/* Flipbook — page turning over pre-rendered page images. No dependencies. */
if (!customElements.get('custom-flipbook')) {
  customElements.define(
    'custom-flipbook',
    class CustomFlipbook extends HTMLElement {
      connectedCallback() {
        this.pages = Array.from(this.querySelectorAll('[data-fb-page]'));
        if (!this.pages.length) return;

        this.prevButton = this.querySelector('[data-fb-prev]');
        this.nextButton = this.querySelector('[data-fb-next]');
        this.counter = this.querySelector('[data-fb-counter]');
        this.total = this.pages.length;
        this.current = 0;
        this.busy = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.go(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.go(1));

        // Arrow keys work once the booklet has focus, without hijacking page scrolling.
        this.setAttribute('tabindex', '0');
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
        });

        this.addTouch();
        this.update();
      }

      addTouch() {
        let startX = null;
        let startY = null;
        this.addEventListener('touchstart', (e) => {
          startX = e.changedTouches[0].clientX;
          startY = e.changedTouches[0].clientY;
        }, { passive: true });

        this.addEventListener('touchend', (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          const dy = e.changedTouches[0].clientY - startY;
          // Ignore mostly-vertical gestures so the page can still be scrolled.
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) this.go(dx < 0 ? 1 : -1);
          startX = null;
          startY = null;
        }, { passive: true });
      }

      go(direction) {
        if (this.busy) return;
        const next = this.current + direction;
        if (next < 0 || next >= this.total) return;

        // Claim the turn synchronously. Setting this inside the promise below would let
        // two quick clicks both pass the guard and start overlapping turns, leaving two
        // pages marked current with animation classes stuck on them.
        this.busy = true;

        const outgoing = this.pages[this.current];
        const incoming = this.pages[next];

        // Decode the incoming page first so the turn doesn't reveal an empty frame.
        // decode() can reject, and on some engines it never settles at all, so race it
        // against a timeout — a slow image should delay the turn, never block it.
        const img = incoming.querySelector('img');
        const decoded = img && img.decode ? img.decode().catch(() => {}) : Promise.resolve();
        const ready = Promise.race([decoded, new Promise((r) => setTimeout(r, 300))]);

        ready.then(() => {
          if (this.reducedMotion) {
            this.commit(outgoing, incoming, next);
            this.busy = false;
            return;
          }

          const outClass = direction > 0 ? 'is-turning-out' : 'is-turning-out-back';
          outgoing.classList.add(outClass);
          incoming.classList.add('is-turning-in', 'is-current');
          incoming.removeAttribute('aria-hidden');

          let settled = false;
          const done = () => {
            if (settled) return;
            settled = true;
            outgoing.classList.remove(outClass, 'is-current');
            outgoing.setAttribute('aria-hidden', 'true');
            incoming.classList.remove('is-turning-in');
            this.current = next;
            this.busy = false;
            this.update();
          };

          outgoing.addEventListener('animationend', done, { once: true });
          // Backgrounded tabs and engines that never composite won't fire animationend,
          // so fall back on a timer rather than leaving the booklet stuck.
          setTimeout(done, 900);
        });
      }

      commit(outgoing, incoming, next) {
        outgoing.classList.remove('is-current');
        outgoing.setAttribute('aria-hidden', 'true');
        incoming.classList.add('is-current');
        incoming.removeAttribute('aria-hidden');
        this.current = next;
        this.update();
      }

      update() {
        if (this.counter) this.counter.textContent = `Page ${this.current + 1} of ${this.total}`;
        if (this.prevButton) this.prevButton.disabled = this.current === 0;
        if (this.nextButton) this.nextButton.disabled = this.current === this.total - 1;
      }
    }
  );
}
